import React from 'react';
import { Reorder, useDragControls } from 'motion/react';
import {
  ArrowDown,
  ArrowUp,
  Columns2,
  GripVertical,
  Heading,
  ImageIcon,
  Info,
  LayoutGrid,
  List,
  Megaphone,
  Plus,
  Quote,
  Table2,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import { ContentBlockType } from '../../../lib/blog-types';
import { cn } from '../../../lib/utils';
import { AddRowButton, AutoTextarea, IconButton, ImageField, TextInput } from '../editor/fields';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

// Content blocks for the blog post editor. The stored format is unchanged:
// `content` is plain text for 'text' and a JSON string for everything else.

export interface ContentBlock {
  id: string;
  blockType: ContentBlockType;
  content: string;
  sortOrder: number;
  metadata?: Record<string, unknown> | null;
}

type Template = { type: ContentBlockType; label: string; icon: React.ComponentType<{ className?: string }>; description: string; defaultContent: string };

export const blockTemplates: Template[] = [
  { type: 'text', label: 'Text', icon: Type, description: 'A paragraph', defaultContent: '' },
  { type: 'heading', label: 'Heading', icon: Heading, description: 'Section heading, H2 to H4', defaultContent: JSON.stringify({ text: '', level: 2 }) },
  { type: 'image', label: 'Image', icon: ImageIcon, description: 'One image with a caption', defaultContent: JSON.stringify({ src: '', alt: '', caption: '' }) },
  { type: 'quote', label: 'Quote', icon: Quote, description: 'Pull quote with attribution', defaultContent: JSON.stringify({ text: '', author: '', role: '' }) },
  { type: 'info-box', label: 'Info box', icon: Info, description: 'Highlighted tip', defaultContent: JSON.stringify({ title: '', text: '', icon: 'info' }) },
  { type: 'list', label: 'List', icon: List, description: 'Bulleted or numbered', defaultContent: JSON.stringify({ items: [''], ordered: false }) },
  { type: 'table', label: 'Table', icon: Table2, description: 'Rows and columns', defaultContent: JSON.stringify({ headers: ['Column 1', 'Column 2'], rows: [['', '']] }) },
  { type: 'two-column', label: 'Two columns', icon: Columns2, description: 'Side-by-side text', defaultContent: JSON.stringify({ left: '', right: '' }) },
  { type: 'gallery', label: 'Gallery', icon: LayoutGrid, description: 'Grid of images', defaultContent: JSON.stringify({ images: [] }) },
  { type: 'cta', label: 'Call to action', icon: Megaphone, description: 'Heading, text and a button', defaultContent: JSON.stringify({ title: '', text: '', buttonText: '', buttonLink: '' }) },
];

export const templateFor = (type: ContentBlockType) => blockTemplates.find((t) => t.type === type);

/** Menu of block types; used between blocks and at the end of the canvas. */
export const BlockMenu: React.FC<{ onPick: (type: ContentBlockType) => void; children: React.ReactNode; align?: 'start' | 'center' | 'end' }> = ({ onPick, children, align = 'center' }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
    <DropdownMenuContent align={align} className="w-64 rounded-xl p-1 font-poppins">
      <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wide text-[color:var(--oa-muted)]">Add a block</DropdownMenuLabel>
      {blockTemplates.map((t) => (
        <DropdownMenuItem key={t.type} onSelect={() => onPick(t.type)} className="gap-2.5 rounded-lg py-1.5">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] text-[color:var(--oa-text)]">
            <t.icon className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0">
            <span className="block text-[12.5px] font-medium text-[color:var(--oa-text)]">{t.label}</span>
            <span className="block truncate text-[11px] text-[color:var(--oa-muted)]">{t.description}</span>
          </span>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/** Thin "+" divider that inserts a block at its position. */
export const InsertPoint: React.FC<{ onPick: (type: ContentBlockType) => void }> = ({ onPick }) => (
  <div className="group relative flex h-6 items-center justify-center">
    <span className="absolute inset-x-6 top-1/2 h-px bg-transparent transition-colors group-hover:bg-[color:color-mix(in_oklab,var(--oa-accent)_25%,transparent)]" />
    <BlockMenu onPick={onPick}>
      <button
        type="button"
        aria-label="Insert a block here"
        className="relative grid h-5 w-5 place-items-center rounded-full border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] text-[color:var(--oa-muted)] opacity-0 transition-opacity hover:border-[color:var(--oa-accent)] hover:text-[color:var(--oa-accent)] focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
      >
        <Plus className="h-3 w-3" />
      </button>
    </BlockMenu>
  </div>
);

/** One block on the canvas: drag handle, type label, move and delete. */
export const BlockCard: React.FC<{
  block: ContentBlock;
  index: number;
  total: number;
  onChange: (content: string) => void;
  onMove: (delta: -1 | 1) => void;
  onDelete: () => void;
}> = ({ block, index, total, onChange, onMove, onDelete }) => {
  const controls = useDragControls();
  const t = templateFor(block.blockType);
  const Icon = t?.icon ?? Type;
  return (
    <Reorder.Item as="div" value={block} dragListener={false} dragControls={controls} className="group/block relative">
      <div className="rounded-xl border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] transition-shadow focus-within:border-[color:var(--oa-line-3)] focus-within:shadow-[0_2px_12px_-6px_rgba(11,15,25,0.15)] hover:border-[color:var(--oa-line-3)]">
        <div className="flex items-center gap-1 border-b border-[color:var(--oa-line-2)] py-1 pl-1 pr-1.5">
          <button
            type="button"
            onPointerDown={(e) => controls.start(e)}
            className="grid h-7 w-6 cursor-grab touch-none place-items-center rounded text-[color:var(--oa-faint)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-muted)] active:cursor-grabbing"
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <Icon className="h-3.5 w-3.5 text-[color:var(--oa-muted)]" />
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[color:var(--oa-muted)]">{t?.label ?? block.blockType}</span>
          <div className="ml-auto flex items-center opacity-60 transition-opacity group-hover/block:opacity-100 group-focus-within/block:opacity-100">
            <IconButton onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></IconButton>
            <IconButton onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></IconButton>
            <IconButton tone="danger" onClick={onDelete} aria-label="Delete block"><Trash2 className="h-3.5 w-3.5" /></IconButton>
          </div>
        </div>
        <div className="px-4 py-3.5">
          <BlockFields block={block} onChange={onChange} />
        </div>
      </div>
    </Reorder.Item>
  );
};

const parse = (content: string): any => {
  try {
    return JSON.parse(content);
  } catch {
    return {};
  }
};

const bare = 'w-full bg-transparent outline-none placeholder:text-[color:var(--oa-faint)]';
const cellInput = 'h-8 min-w-0 flex-1 rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-2 text-[13px] text-[color:var(--oa-text)] placeholder:text-[color:var(--oa-faint)] outline-none focus:border-[color:var(--oa-accent)] focus:ring-2 focus:ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]';

/** Type-specific inputs, styled to read roughly like the published post. */
const BlockFields: React.FC<{ block: ContentBlock; onChange: (content: string) => void }> = ({ block, onChange }) => {
  const data = block.blockType === 'text' ? null : parse(block.content);
  const set = (patch: Record<string, unknown>) => onChange(JSON.stringify({ ...data, ...patch }));

  switch (block.blockType) {
    case 'text':
      return <AutoTextarea bare minRows={3} value={block.content} onChange={(e) => onChange(e.target.value)} placeholder="Write a paragraph…" className="text-[15px] leading-7 text-[color:var(--oa-text)]" />;

    case 'heading': {
      const level = data.level || 2;
      return (
        <div className="flex items-center gap-3">
          <div className="inline-flex shrink-0 rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] p-0.5">
            {[2, 3, 4].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => set({ level: l })}
                className={cn('h-6 rounded px-2 font-mono text-[11px] font-semibold', level === l ? 'bg-[color:var(--oa-card)] text-[color:var(--oa-text)] shadow-[0_1px_2px_rgba(11,15,25,0.08)]' : 'text-[color:var(--oa-muted)] hover:text-[color:var(--oa-text)]')}
              >
                H{l}
              </button>
            ))}
          </div>
          <input
            value={data.text || ''}
            onChange={(e) => set({ text: e.target.value })}
            placeholder="Heading"
            className={cn(bare, 'font-montserrat font-extrabold tracking-[-0.01em] text-[color:var(--oa-text)]', level === 2 ? 'text-[22px]' : level === 3 ? 'text-[18px]' : 'text-[15.5px]')}
          />
        </div>
      );
    }

    case 'image':
      return (
        <div className="space-y-2.5">
          <ImageField value={data.src || ''} onChange={(src) => set({ src })} placeholder="Image URL" />
          <div className="grid gap-2 sm:grid-cols-2">
            <TextInput value={data.alt || ''} onChange={(e) => set({ alt: e.target.value })} placeholder="Alt text (describe the image)" />
            <TextInput value={data.caption || ''} onChange={(e) => set({ caption: e.target.value })} placeholder="Caption (optional)" />
          </div>
        </div>
      );

    case 'quote':
      return (
        <div className="border-l-[3px] border-[color:var(--oa-accent)] pl-4">
          <AutoTextarea bare minRows={2} value={data.text || ''} onChange={(e) => set({ text: e.target.value })} placeholder="Quote" className="font-montserrat text-[17px] font-semibold italic leading-relaxed text-[color:var(--oa-text)]" />
          <div className="mt-2 flex flex-wrap gap-2 text-[13px]">
            <input value={data.author || ''} onChange={(e) => set({ author: e.target.value })} placeholder="Who said it" className={cn(bare, 'w-auto flex-1 font-medium text-[color:var(--oa-text)]')} />
            <input value={data.role || ''} onChange={(e) => set({ role: e.target.value })} placeholder="Their role" className={cn(bare, 'w-auto flex-1 text-[color:var(--oa-muted)]')} />
          </div>
        </div>
      );

    case 'info-box':
      return (
        <div className="rounded-lg border border-[color:color-mix(in_oklab,var(--oa-accent)_25%,transparent)] bg-[color:var(--oa-accent-soft)] px-4 py-3">
          <input value={data.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Tip title" className={cn(bare, 'text-[14px] font-semibold text-[color:var(--oa-accent)]')} />
          <AutoTextarea bare minRows={2} value={data.text || ''} onChange={(e) => set({ text: e.target.value })} placeholder="The tip itself" className="mt-1 text-[13.5px] leading-6 text-[color:var(--oa-text)]" />
        </div>
      );

    case 'list': {
      const items: string[] = data.items?.length ? data.items : [''];
      const setItems = (next: string[]) => set({ items: next.length ? next : [''] });
      return (
        <div className="space-y-1.5">
          <div className="mb-2 inline-flex rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] p-0.5 text-[11.5px]">
            {[false, true].map((o) => (
              <button key={String(o)} type="button" onClick={() => set({ ordered: o })} className={cn('h-6 rounded px-2 font-medium', !!data.ordered === o ? 'bg-[color:var(--oa-card)] text-[color:var(--oa-text)] shadow-[0_1px_2px_rgba(11,15,25,0.08)]' : 'text-[color:var(--oa-muted)]')}>
                {o ? 'Numbered' : 'Bullets'}
              </button>
            ))}
          </div>
          {items.map((item, i) => (
            <div key={i} className="group/row flex items-center gap-2">
              <span className="w-5 shrink-0 text-right text-[13px] tabular-nums text-[color:var(--oa-muted)]">{data.ordered ? `${i + 1}.` : '•'}</span>
              <input
                value={item}
                onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setItems([...items.slice(0, i + 1), '', ...items.slice(i + 1)]);
                  }
                }}
                placeholder="List item"
                className={cn(bare, 'h-7 text-[14.5px] text-[color:var(--oa-text)]')}
              />
              <IconButton tone="danger" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Remove item" className="opacity-0 group-hover/row:opacity-100">
                <X className="h-3.5 w-3.5" />
              </IconButton>
            </div>
          ))}
          <AddRowButton onClick={() => setItems([...items, ''])}>Add item</AddRowButton>
        </div>
      );
    }

    case 'two-column':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          {(['left', 'right'] as const).map((side) => (
            <div key={side} className="rounded-lg border border-dashed border-[color:var(--oa-line-2)] px-3 py-2">
              <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--oa-faint)]">{side}</p>
              <AutoTextarea bare minRows={3} value={data[side] || ''} onChange={(e) => set({ [side]: e.target.value })} placeholder={`${side === 'left' ? 'Left' : 'Right'} column`} className="text-[14px] leading-6 text-[color:var(--oa-text)]" />
            </div>
          ))}
        </div>
      );

    case 'cta':
      return (
        // Previews the site's black CTA band, so it stays black in both admin modes.
        <div className="rounded-lg bg-[#0B0F19] px-5 py-4 text-white">
          <input value={data.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Heading" className={cn(bare, 'font-montserrat text-[18px] font-extrabold placeholder:text-white/30')} />
          <AutoTextarea bare minRows={2} value={data.text || ''} onChange={(e) => set({ text: e.target.value })} placeholder="Supporting text" className="mt-1 text-[13.5px] leading-6 text-white/80 placeholder:text-white/30" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input value={data.buttonText || ''} onChange={(e) => set({ buttonText: e.target.value })} placeholder="Button label" className="h-8 w-40 rounded-md bg-[color:var(--oa-accent)] px-3 text-[12.5px] font-semibold text-[color:var(--oa-on-accent)] placeholder:text-[color:color-mix(in_oklab,var(--oa-on-accent)_70%,transparent)] outline-none" />
            <input value={data.buttonLink || ''} onChange={(e) => set({ buttonLink: e.target.value })} placeholder="/link or https://…" className="h-8 min-w-0 flex-1 rounded-md border border-white/15 bg-white/5 px-2.5 font-mono text-[12px] text-white placeholder:text-white/30 outline-none focus:border-white/40" />
          </div>
        </div>
      );

    case 'table': {
      const headers: string[] = data.headers || ['Column 1', 'Column 2'];
      const rows: string[][] = data.rows || [headers.map(() => '')];
      return (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-1">
              <thead>
                <tr>
                  {headers.map((h, i) => (
                    <th key={i} className="p-0">
                      <div className="flex items-center gap-1">
                        <input value={h} onChange={(e) => set({ headers: headers.map((x, j) => (j === i ? e.target.value : x)) })} placeholder={`Column ${i + 1}`} className={cn(cellInput, 'bg-[color:var(--oa-raised)] font-semibold')} />
                        {headers.length > 1 ? (
                          <IconButton tone="danger" onClick={() => set({ headers: headers.filter((_, j) => j !== i), rows: rows.map((r) => r.filter((_, j) => j !== i)) })} aria-label="Remove column">
                            <X className="h-3.5 w-3.5" />
                          </IconButton>
                        ) : null}
                      </div>
                    </th>
                  ))}
                  <th className="w-8 p-0" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c} className="p-0">
                        <input value={cell} onChange={(e) => set({ rows: rows.map((x, i) => (i === r ? x.map((y, j) => (j === c ? e.target.value : y)) : x)) })} className={cellInput} />
                      </td>
                    ))}
                    <td className="p-0">
                      <IconButton tone="danger" onClick={() => set({ rows: rows.length > 1 ? rows.filter((_, i) => i !== r) : [headers.map(() => '')] })} aria-label="Remove row">
                        <X className="h-3.5 w-3.5" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-1">
            <AddRowButton onClick={() => set({ rows: [...rows, headers.map(() => '')] })}>Row</AddRowButton>
            <AddRowButton onClick={() => set({ headers: [...headers, `Column ${headers.length + 1}`], rows: rows.map((r) => [...r, '']) })}>Column</AddRowButton>
          </div>
        </div>
      );
    }

    case 'gallery': {
      type Img = { src: string; alt: string; label?: string };
      const images: Img[] = data.images || [];
      const setImages = (next: Img[]) => set({ images: next });
      return (
        <div className="space-y-3">
          {images.length ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {images.map((img, i) => (
                <div key={i} className="relative rounded-lg border border-[color:var(--oa-line-2)] p-2">
                  <IconButton tone="danger" onClick={() => setImages(images.filter((_, j) => j !== i))} aria-label="Remove image" className="absolute right-1 top-1 z-10 bg-[color:color-mix(in_oklab,var(--oa-card)_90%,transparent)]">
                    <X className="h-3.5 w-3.5" />
                  </IconButton>
                  <ImageField value={img.src || ''} onChange={(src) => setImages(images.map((x, j) => (j === i ? { ...x, src } : x)))} aspect="aspect-[4/3]" placeholder="Image URL" />
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <TextInput value={img.alt || ''} onChange={(e) => setImages(images.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)))} placeholder="Alt text" />
                    <TextInput value={img.label || ''} onChange={(e) => setImages(images.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Label" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12.5px] text-[color:var(--oa-muted)]">No images yet.</p>
          )}
          <AddRowButton onClick={() => setImages([...images, { src: '', alt: '', label: '' }])}>Add image</AddRowButton>
        </div>
      );
    }

    default:
      return <AutoTextarea minRows={3} value={block.content} onChange={(e) => onChange(e.target.value)} placeholder="Content" />;
  }
};
